import React, { useEffect, useRef, useState } from "react";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { useProSidebar } from "react-pro-sidebar";
import { SidebarWrapper } from "./styles";
import NavConfig, { NavItem } from "./navConfig";
import { useAuth } from "contexts/AuthContext";
import SubMenuWrapper from "./SubMenuWrapper";

interface SidebarComponentProps {
  items?: NavItem[];
}

function SidebarComponent({ items = NavConfig }: SidebarComponentProps) {
  const { collapseSidebar, collapsed } = useProSidebar();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isTablet = useMediaQuery({ maxWidth: 820 });
  const isDesktop = useMediaQuery({ minWidth: 820 });
  const { userGroups } = useAuth();
  const [menuKey, setMenuKey] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect para inicializar o sidebar corretamente
  useEffect(() => {
    // Pequeno delay para garantir que o estado foi aplicado e DOM atualizado
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      // Limpa também o timeout de hover se existir
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [collapseSidebar]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !collapsed && isTablet) {
        setMenuKey(k => k + 1);
        setTimeout(() => {
          collapseSidebar(true);
        }, 50);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarRef, collapsed, isTablet]);

  const handleClick = () => {
    toast.warning("Esta página está em desenvolvimento!");
  };

  const hasPermission = (groups: string[] | undefined): boolean => {
    if (groups?.length === 0) return true;
    return groups ? groups.some((permission) => userGroups.includes(permission)) : false;
  };

  const renderMenuItems = (items: NavItem[]) =>
    items.map((item, index) => {
      if (!hasPermission(item.permissions)) return null;

      const isActive = item.path === location.pathname;

      // Se tem filhos, use o componente separado SubMenuWrapper (para evitar bugs de renderização do react-pro-sidebar)
      if (item.children?.length) {
        const hasActiveChild = item.children.some(child => child.path === location.pathname);
        return (
          <React.Fragment key={`submenu-${index}`}>
            <SubMenuWrapper
              item={item}
              isActive={isActive}
              hasActiveChild={hasActiveChild}
              handleClick={handleClick}
            />
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={index}>
          <MenuItem
            component={<Link to={item.path || "#"} />}
            className={`menuItem${isActive ? " active" : ""}`}
            icon={<item.icon className={`menuIcon${isActive ? " active" : ""}`} style={{ color: "#A2A2A2", heigth: "10px" }} />}
            onClick={() => {
              if (!item.path) {
                handleClick();
              }
            }}
          >
            <b className={`menuText${isActive ? " active" : ""}`}>{item.title}</b>
          </MenuItem>
        </React.Fragment>
      );
    });

  // Função para fechar todos os submenus
  const closeAllSubmenus = React.useCallback(() => {
    setMenuKey(prev => prev + 1);
  }, []);

  return (
    <SidebarWrapper
      ref={sidebarRef}
      className={!isInitialized ? "sidebar-loading" : ""}
      onMouseEnter={() => isDesktop && collapseSidebar(false)}  // ← IMEDIATO
      onMouseLeave={() => {
        // Limpa timeout de expansão se estiver pendente
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }

        if (isDesktop) {
          // closeAllSubmenus();
          setTimeout(() => {
            collapseSidebar(true);
          }, 0);
        }
      }}
    >
      <Sidebar
        className="sidebarContainer"
        defaultCollapsed
        collapsedWidth={isTablet ? "0px" : "80px"}
      >
        <Menu key={`menu-${menuKey}`} className="menu">
          {isInitialized ? renderMenuItems(items) : null}
        </Menu>
      </Sidebar>
    </SidebarWrapper>
  );
}

export default SidebarComponent;
